import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Ruler, Box } from 'lucide-react';
import { toast } from 'sonner';
import { SidePanel } from '../components/SidePanel';
import * as api from '../api';

const CATEGORIES_U = [
  { value: 'quantite', label: 'Quantité', bg: '#F5F3FF', color: '#6D28D9' },
  { value: 'masse',    label: 'Masse',    bg: '#FEF9C3', color: '#854D0E' },
  { value: 'volume',   label: 'Volume',   bg: 'var(--primary-50)', color: 'var(--primary-light)' },
];

const TYPES_C = ['boite', 'carton', 'palette', 'sac', 'bidon', 'flacon', 'rouleau'];

const EMPTY_U = { symbole: '', nom_fr: '', categorie: '', facteur_si: '' };
const EMPTY_C = { type: '', contenance: '', unite_ref: '' };

export default function UnitesContenants() {
  const [unites, setUnites]     = useState([]);
  const [contenants, setContenants] = useState([]);
  const [loadingU, setLoadingU] = useState(true);
  const [loadingC, setLoadingC] = useState(true);

  // Panel unité
  const [panelU, setPanelU]   = useState(false);
  const [editU, setEditU]     = useState(null);
  const [formU, setFormU]     = useState(EMPTY_U);
  const [savingU, setSavingU] = useState(false);

  // Panel contenant
  const [panelC, setPanelC]   = useState(false);
  const [editC, setEditC]     = useState(null);
  const [formC, setFormC]     = useState(EMPTY_C);
  const [savingC, setSavingC] = useState(false);

  const loadU = useCallback(() => {
    setLoadingU(true);
    api.getUnites().then(r => setUnites(r.data || [])).catch(() => toast.error('Erreur unités')).finally(() => setLoadingU(false));
  }, []);
  const loadC = useCallback(() => {
    setLoadingC(true);
    api.getContenants().then(r => setContenants(r.data || [])).catch(() => toast.error('Erreur contenants')).finally(() => setLoadingC(false));
  }, []);

  useEffect(() => { loadU(); loadC(); }, [loadU, loadC]);

  // ── Unité CRUD ──────────────────────────────────────────────────────────────
  const openNewU = () => { setEditU(null); setFormU(EMPTY_U); setPanelU(true); };
  const openEditU = (u) => { setEditU(u); setFormU({ symbole: u.symbole, nom_fr: u.nom_fr, categorie: u.categorie, facteur_si: String(u.facteur_si) }); setPanelU(true); };
  const saveU = async () => {
    if (!formU.symbole || !formU.nom_fr || !formU.categorie || formU.facteur_si === '') { toast.error('Tous les champs sont obligatoires'); return; }
    setSavingU(true);
    try {
      const payload = { ...formU, facteur_si: parseFloat(formU.facteur_si) };
      if (editU) { await api.updateUnite(editU.unite_id, payload); toast.success('Unité modifiée'); }
      else { await api.createUnite(payload); toast.success('Unité créée'); }
      setPanelU(false); loadU();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSavingU(false); }
  };
  const delU = async (u) => {
    if (!window.confirm(`Supprimer l'unité "${u.symbole}" ?`)) return;
    try { await api.deleteUnite(u.unite_id); toast.success('Unité supprimée'); loadU(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  // ── Contenant CRUD ──────────────────────────────────────────────────────────
  const openNewC = () => { setEditC(null); setFormC(EMPTY_C); setPanelC(true); };
  const openEditC = (c) => { setEditC(c); setFormC({ type: c.type, contenance: String(c.contenance), unite_ref: c.unite_ref }); setPanelC(true); };
  const saveC = async () => {
    if (!formC.type || !formC.contenance || !formC.unite_ref) { toast.error('Tous les champs sont obligatoires'); return; }
    setSavingC(true);
    try {
      const payload = { ...formC, contenance: parseFloat(formC.contenance) };
      if (editC) { await api.updateContenant(editC.contenant_id, payload); toast.success('Contenant modifié'); }
      else { await api.createContenant(payload); toast.success('Contenant créé'); }
      setPanelC(false); loadC();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setSavingC(false); }
  };
  const delC = async (c) => {
    if (!window.confirm(`Supprimer ce contenant ?`)) return;
    try { await api.deleteContenant(c.contenant_id); toast.success('Contenant supprimé'); loadC(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  // ── Regroupement unités ──────────────────────────────────────────────────────
  const grouped = CATEGORIES_U.map(cat => ({
    ...cat,
    items: unites.filter(u => u.categorie === cat.value).sort((a, b) => a.facteur_si - b.facteur_si),
  })).filter(g => g.items.length > 0);

  const groupedC = TYPES_C.map(type => ({
    type, items: contenants.filter(c => c.type === type).sort((a, b) => a.contenance - b.contenance),
  })).filter(g => g.items.length > 0);

  const inp = "w-full px-3 py-2 border rounded-lg text-sm focus:outline-none";
  const inpSt = { borderColor: 'var(--border)' };
  const lbl = "block text-xs font-semibold uppercase tracking-wider mb-1";
  const lblSt = { color: 'var(--text-muted)' };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-solid)',  }}>Unités & Contenants</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Référentiels utilisés dans les fiches articles</p>
      </div>

      {/* ── Unités ─────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
            <Ruler size={14} /> Unités de mesure
          </h2>
          <button onClick={openNewU} data-testid="add-unite-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            <Plus size={12} /> Nouvelle unité
          </button>
        </div>
        {loadingU ? <div className="text-center py-8 text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement...</div> : (
          <div className="space-y-4">
            {grouped.map(g => (
              <div key={g.value}>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mb-2" style={{ background: g.bg, color: g.color }}>{g.label}</span>
                <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                  <table className="w-full text-sm">
                    <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid #E6E6E1' }}>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Symbole</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Nom</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Facteur SI</th>
                      <th />
                    </tr></thead>
                    <tbody>
                      {g.items.map(u => (
                        <tr key={u.unite_id} className="hover:bg-gray-50/50" style={{ borderTop: '1px solid #F2F2EE' }} data-testid={`unite-row-${u.symbole}`}>
                          <td className="px-4 py-2.5">
                            <span className="font-bold px-2 py-0.5 rounded text-xs" style={{ background: g.bg, color: g.color }}>{u.symbole}</span>
                          </td>
                          <td className="px-4 py-2.5 font-medium" style={{ color: 'var(--text)' }}>{u.nom_fr}</td>
                          <td className="px-4 py-2.5 text-xs" style={{ color: 'var(--text-muted)' }}>× {u.facteur_si}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => openEditU(u)} className="p-1.5 rounded hover:bg-yellow-50" style={{ color: '#B45309' }}><Pencil size={13} /></button>
                              <button onClick={() => delU(u)} className="p-1.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {grouped.length === 0 && <p className="text-sm text-center py-6" style={{ color: 'var(--text-placeholder)' }}>Aucune unité</p>}
          </div>
        )}
      </div>

      {/* ── Contenants ─────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
            <Box size={14} /> Contenants
          </h2>
          <button onClick={openNewC} data-testid="add-contenant-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            <Plus size={12} /> Nouveau contenant
          </button>
        </div>
        {loadingC ? <div className="text-center py-8 text-sm" style={{ color: 'var(--text-placeholder)' }}>Chargement...</div> : (
          <div className="space-y-4">
            {groupedC.map(g => (
              <div key={g.type}>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold mb-2" style={{ background: '#F3F4F6', color: '#374151', textTransform: 'capitalize' }}>{g.type}</span>
                <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                  <table className="w-full text-sm">
                    <thead><tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid #E6E6E1' }}>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Type</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Contenance</th>
                      <th className="text-left px-4 py-2 text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Unité</th>
                      <th />
                    </tr></thead>
                    <tbody>
                      {g.items.map(c => (
                        <tr key={c.contenant_id} className="hover:bg-gray-50/50" style={{ borderTop: '1px solid #F2F2EE' }} data-testid={`contenant-row-${c.contenant_id}`}>
                          <td className="px-4 py-2.5 font-medium capitalize" style={{ color: 'var(--text)' }}>{c.type}</td>
                          <td className="px-4 py-2.5" style={{ color: 'var(--text-secondary)' }}>{c.contenance}</td>
                          <td className="px-4 py-2.5 text-xs font-semibold" style={{ color: 'var(--primary-solid)' }}>{c.unite_ref}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex gap-1 justify-end">
                              <button onClick={() => openEditC(c)} className="p-1.5 rounded hover:bg-yellow-50" style={{ color: '#B45309' }}><Pencil size={13} /></button>
                              <button onClick={() => delC(c)} className="p-1.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}><Trash2 size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
            {groupedC.length === 0 && <p className="text-sm text-center py-6" style={{ color: 'var(--text-placeholder)' }}>Aucun contenant</p>}
          </div>
        )}
      </div>

      {/* ── Panel Unité ─────────────────────────────────────────────────────── */}
      <SidePanel open={panelU} onOpenChange={v => { if (!v) setPanelU(false); }}
        title={editU ? 'Modifier l\'unité' : 'Nouvelle unité'}
        footer={
          <div className="flex gap-3 justify-end">
            <button onClick={() => setPanelU(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Annuler</button>
            <button onClick={saveU} disabled={savingU} data-testid="save-unite-btn"
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              {savingU ? 'Enregistrement...' : (editU ? 'Modifier' : 'Créer')}
            </button>
          </div>
        }>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl} style={lblSt}>Symbole *</label>
              <input value={formU.symbole} onChange={e => setFormU(f => ({ ...f, symbole: e.target.value }))}
                placeholder="ex: pce, kg, L" disabled={!!editU}
                data-testid="unite-symbole-input" className={inp} style={{ ...inpSt, background: editU ? 'var(--bg-subtle)' : 'white' }} />
              {editU && <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>Non modifiable</p>}
            </div>
            <div>
              <label className={lbl} style={lblSt}>Catégorie *</label>
              <select value={formU.categorie} onChange={e => setFormU(f => ({ ...f, categorie: e.target.value }))}
                data-testid="unite-categorie-select" className={inp} style={inpSt}>
                <option value="">— Catégorie —</option>
                {CATEGORIES_U.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className={lbl} style={lblSt}>Nom complet *</label>
            <input value={formU.nom_fr} onChange={e => setFormU(f => ({ ...f, nom_fr: e.target.value }))}
              placeholder="ex: Litre, Kilogramme, Pièce"
              data-testid="unite-nom-input" className={inp} style={inpSt} />
          </div>
          <div>
            <label className={lbl} style={lblSt}>Facteur de conversion *</label>
            <input type="number" step="any" min="0" value={formU.facteur_si}
              onChange={e => setFormU(f => ({ ...f, facteur_si: e.target.value }))}
              placeholder="ex: 1 pour kg, 0.001 pour g, 1000 pour t"
              data-testid="unite-facteur-input" className={inp} style={inpSt} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-placeholder)' }}>
              {formU.categorie === 'volume'   && 'Base = L. mL = 0.001, cL = 0.01'}
              {formU.categorie === 'masse'    && 'Base = kg. g = 0.001, t = 1000'}
              {formU.categorie === 'quantite' && 'Base = 1 unité (pce, h, m...)'}
            </p>
          </div>
        </div>
      </SidePanel>

      {/* ── Panel Contenant ──────────────────────────────────────────────────── */}
      <SidePanel open={panelC} onOpenChange={v => { if (!v) setPanelC(false); }}
        title={editC ? 'Modifier le contenant' : 'Nouveau contenant'}
        footer={
          <div className="flex gap-3 justify-end">
            <button onClick={() => setPanelC(false)} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>Annuler</button>
            <button onClick={saveC} disabled={savingC} data-testid="save-contenant-btn"
              className="px-5 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              {savingC ? 'Enregistrement...' : (editC ? 'Modifier' : 'Créer')}
            </button>
          </div>
        }>
        <div className="space-y-4">
          <div>
            <label className={lbl} style={lblSt}>Type *</label>
            <select value={formC.type} onChange={e => setFormC(f => ({ ...f, type: e.target.value }))}
              data-testid="contenant-type-select" className={inp} style={inpSt}>
              <option value="">— Type —</option>
              {TYPES_C.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={lbl} style={lblSt}>Contenance *</label>
              <input type="number" min="0" step="any" value={formC.contenance}
                onChange={e => setFormC(f => ({ ...f, contenance: e.target.value }))}
                placeholder="ex: 25" data-testid="contenant-contenance-input"
                className={inp} style={inpSt} />
            </div>
            <div>
              <label className={lbl} style={lblSt}>Unité *</label>
              <select value={formC.unite_ref} onChange={e => setFormC(f => ({ ...f, unite_ref: e.target.value }))}
                data-testid="contenant-unite-select" className={inp} style={inpSt}>
                <option value="">— Unité —</option>
                {unites.map(u => <option key={u.unite_id} value={u.symbole}>{u.symbole} — {u.nom_fr}</option>)}
              </select>
            </div>
          </div>
        </div>
      </SidePanel>
    </div>
  );
}
